<template>
    <div
        class="hts-travel-details"
        :class="CClass"
        @click="handleClick">
        <hts-travel-details-location
            v-if="!showTrip && !isPartnerJourney"
            :edit-time="editTime"
            :location="startAtLocation"
            :time-at="startAt"
            :time-at-estimate="startAtEstimate"
            :time-at-actual-estimate="startAtActualEstimate"
            :time-at-actual="startAtActual"
            :travel-index="travelIndex"
            :travel-stage-index="travelStageIndex"
            :has-error="errorList.includes('start-empty') || errorList.includes('start-invalid')"
            :show-vehicle="startLocationVehicleVisible"
            error-message="Thời gian khởi hành không hợp lệ"
            :process-alert-status="processAlertStatus"
            @update-time="handleUpdateStartAt" />
        <template
            v-for="(item, itemIndex) in stagies"
            :key="itemIndex">
            <hts-travel-details-stage-item
                v-if="item.type === 'stage'"
                :travel-node-data-arr="travelNodeDataArr"
                :stage="item"
                :journey-id="journeyId"
                :stage-index="itemIndex"
                :next-stage="stagies[itemIndex + 1] || null"
                :stop-at-actual="stopAtActual"
                :travel-details="travelDetails"
                :expanded="expandedStagies.includes(itemIndex)"
                :is-last="itemIndex === stagies.length - 1"
                :start="start"
                :end="end"
                :edit-time="editTime"
                :show-trip="showTrip"
                :travel-index="travelIndex"
                :travel-stage-index="travelStageIndex"
                :error-list="errorList"
                :item-index="itemIndex"
                :date-format="dateFormat"
                :clearable="true"
                :files="getFiles(item.index)"
                @clickStep="handleClickStep($event, itemIndex)"
                @update-time="handleUpdateTime($event)" />
        </template>
        <hts-travel-details-location
            v-if="!showTrip && !isPartnerJourney"
            label="Kết thúc"
            :edit-time="editTime"
            :location="stopAtLocation"
            :time-at="stopAt"
            :time-at-estimate="stopAtEstimate"
            :time-at-actual-estimate="stopAtActualEstimate"
            :time-at-actual="stopAtActual"
            :travel-index="travelIndex"
            :travel-stage-index="travelStageIndex"
            :has-error="errorList.includes('stop')"
            error-message="Thời gian kết thúc không hợp lệ"
            @update-time="handleUpdateStopAt" />
    </div>
</template>

<script setup lang="ts">
import dayjs from 'dayjs'

const { $dayjs, $formatters } = useNuxtApp()
const props = defineProps({
  journeyId: String,
  partnerId: String,
  travelDetails: {
    type: Array as PropType<any[]>,
    required: true
  },
  travelNodeDataArr: {
    type: Array as PropType<any[]>,
    default: () => []
  },
  direction: {
    type: String,
    default: 'vertical'
  },
  stageStart: Number,
  stageEnd: Number,
  start: Number,
  end: Number,
  editTime: Boolean,
  showTrip: Boolean,
  travelStageIndex: Number,
  travelIndex: Number,
  startAt: { type: [String, Number] as PropType<number | string | null> },
  startAtEstimate: { type: [String, Number] as PropType<number | string | null> },
  startAtActualEstimate: { type: [String, Number] as PropType<number | string | null> },
  startAtActual: { type: [String, Number] as PropType<number | string | null> },
  startAtParking: Boolean,
  startAtLocation: Object,
  stopAt: { type: [String, Number] as PropType<number | string | null> },
  stopAtEstimate: { type: [String, Number] as PropType<number | string | null> },
  stopAtActualEstimate: { type: [String, Number] as PropType<number | string | null> },
  stopAtActual: { type: [String, Number] as PropType<number | string | null> },
  stopAtParking: Boolean,
  stopAtLocation: Object,
  dateFormat: String,
  processAlertStatus: Number,
  files: { type: Array<any> }
})

const emits = defineEmits(['clickStep', 'update-start-at', 'update-stop-at', 'editing'])

const { travelDetails, direction, stageStart, stageEnd, start, startAtActual, startAtParking, end, stopAtActual, stopAtParking, showTrip, partnerId, files } =
  toRefs(props)
const isPartnerJourney = computed(() => !!partnerId?.value)
const errorList = ref<string[]>([])

const stagies = computed<any[]>(() => {
  if (showTrip.value) {
    return (travelDetails?.value || []).filter((item: any) => {
      return item.type === 'stage'
    })
  }
  return (travelDetails?.value || [])
    .map((t: any, i: number) => {
      Object.assign(t, {
        travelIndex: i,
        ata: $formatters.datetime(t.ata, 'YYYY-MM-DD HH:mm'),
        atd: $formatters.datetime(t.atd, 'YYYY-MM-DD HH:mm')
      })
      return t
    })
    .filter((item: any) => {
      const { index } = item
      return (
        item.type === 'stage' &&
        (typeof stageStart?.value !== 'number' || (typeof stageStart?.value === 'number' && index >= stageStart.value)) &&
        (typeof stageEnd?.value !== 'number' || (typeof stageEnd?.value === 'number' && index <= stageEnd.value))
      )
    })
})

const firstStage = computed(() => stagies.value[0] || null)
const startLocationVehicleVisible = computed(() => {
  return !firstStage.value?.ata
})

const inputValidate = () => {
  errorList.value = []
  const firstStage = stagies.value[0] || null
  const lastStage = stagies.value[stagies.value.length - 1] || null
  let validIndex = -2
  // Not partner journey - check start at
  if (!isPartnerJourney.value) {
    if (startAtActual?.value) {
      validIndex = -1
      if (firstStage && dayjs(firstStage.ata).isValid() && dayjs(startAtActual?.value).isValid()) {
        if (dayjs(startAtActual?.value).isAfter(dayjs(firstStage.ata), 'm')) {
          errorList.value.push('0-in-gt-start')
          errorList.value.push('start-invalid')
        }
      }
    } else if (startAtParking?.value) {
      errorList.value.push('start-empty')
    }
  } else {
    validIndex = -1
  }

  // Not partner journey - check stop at
  if (!isPartnerJourney.value) {
    if (stopAtActual?.value) {
      if (lastStage && dayjs(lastStage.atd).isValid() && dayjs(stopAtActual?.value).isValid()) {
        if (dayjs(stopAtActual?.value).isBefore(dayjs(lastStage.atd), 'm')) {
          errorList.value.push(stagies.value.length - 1 + '-out-gt-stop')
          errorList.value.push('stop-invalid')
        }
      }
    }
  }
  stagies.value.forEach((item: any, index: number) => {
    if (
      !(
        (typeof start?.value !== 'number' || (typeof start?.value === 'number' && item.travelIndex >= start.value)) &&
        (typeof end?.value !== 'number' || (typeof end?.value === 'number' && item.travelIndex <= end.value))
      )
    ) {
      return
    }
    const { ata, atd } = item

    const stageTasks = getStageTasks(item.travelIndex)
    if (ata) {
      if (!dayjs(ata).isValid()) {
        errorList.value.push(index + '-in-invalid')
      }
      stageTasks.forEach((item: any) => {
        if (dayjs(item.ata).isValid() && dayjs(ata).isAfter(dayjs(item.ata), 'm')) {
          errorList.value.push(index + '-in-invalid')
        }
        if (dayjs(item.atd).isValid() && dayjs(ata).isAfter(dayjs(item.atd), 'm')) {
          errorList.value.push(index + '-in-invalid')
        }
      })
      if (index > validIndex + 1) {
        errorList.value.push(index + '-in')
      }
    }

    if (atd) {
      const notValidTasks = stageTasks.filter((item: any) => {
        return !item.ata || !item.atd
      })
      if (notValidTasks.length) {
        errorList.value.push(index + '-out-task')
      }
      if (!dayjs(atd).isValid()) {
        errorList.value.push(index + '-out-invalid')
      }
      stageTasks.forEach((item: any) => {
        if (dayjs(item.ata).isValid() && dayjs(atd).isBefore(dayjs(item.ata), 'm')) {
          errorList.value.push(index + '-out-task')
        }
        if (dayjs(item.atd).isValid() && dayjs(atd).isBefore(dayjs(item.atd), 'm')) {
          errorList.value.push(index + '-out-task')
        }
      })
      if (!ata) {
        errorList.value.push(index + '-out-empty')
      } else if (dayjs(ata).isAfter(dayjs(atd), 'm')) {
        errorList.value.push(index + '-out')
      }
      validIndex = index
    }
  })
  // Not partner journey - check stop at
  if (!isPartnerJourney.value) {
    if (stopAtActual?.value) {
      if (validIndex < stagies.value.length - 1) {
        errorList.value.push('stop')
      }
    }
  }
}
const inputValidateDebounce = $lodash.debounce(inputValidate, 300)

const getStageTasks = (stageIndex: number) => {
  return (travelDetails?.value || []).filter((item: any, i: number) => {
    return (
      item.type === 'task' &&
      item.stageIndex === stageIndex &&
      (typeof start?.value !== 'number' || (typeof start?.value === 'number' && i >= start.value)) &&
      (typeof end?.value !== 'number' || (typeof end?.value === 'number' && i <= end.value))
    )
  })
}

const stageVisible = (stage: any) => {
  const { travelIndex } = stage
  if (showTrip.value) {
    return true
  }
  return (
    (typeof start?.value !== 'number' || (typeof start?.value === 'number' && travelIndex >= start.value)) &&
    (typeof end?.value !== 'number' || (typeof end?.value === 'number' && travelIndex <= end.value))
  )
}
const expandedStagies = ref<number[]>([])

const CClass = computed(() => {
  let classData = ''

  const directions: String[] = ['horizontal', 'vertical']

  if (directions.includes(direction.value)) {
    classData += direction.value
  }

  return classData
})

onMounted(() => {})

const handleClickStep = (step: any, itemIndex: number) => {
  emits('clickStep', step)
  const exists = expandedStagies.value.findIndex((e) => e === itemIndex)
  if (exists >= 0) {
    expandedStagies.value.splice(exists, 1)
  } else {
    expandedStagies.value.push(itemIndex)
  }
}

const toggleExpandCollapse = (val: boolean) => {
  if (val) {
    expandedStagies.value = stagies.value.map((i, index) => index)
  } else {
    expandedStagies.value = []
  }
}

const handleClick = (e: any) => {
  e.stopPropagation()
}
const handleUpdateTime = (val: any) => {
  emits('editing')
  const { value, type, item } = val
  const { travelIndex } = item
  if (travelDetails.value[travelIndex]) {
    travelDetails.value[travelIndex][type] = value
  }

  nextTick(() => {
    inputValidateDebounce()
  })
}
const handleUpdateStartAt = (val: any) => {
  emits('update-start-at', val)
  nextTick(() => {
    inputValidateDebounce()
  })
}
const handleUpdateStopAt = (val: any) => {
  emits('update-stop-at', val)
  nextTick(() => {
    inputValidateDebounce()
  })
}

const validate = async () => {
  inputValidate()
  await new Promise((resolve: any) => {
    nextTick(() => {
      resolve()
    })
  })
  return errorList.value.length > 0
}

const getFiles = (index: number) => {
  if (!files?.value?.length) {
    return []
  }

  return files.value.filter((item) => item.travelStageIndex === index)
}

defineExpose({
  errorList,
  toggleExpandCollapse,
  validate
})
</script>
